"use client";

import { ClientAvatarType, ProcessingJobType } from "@prisma/client";
import { useQueryClient } from "@tanstack/react-query";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { Components } from "@/app/(components)/_components/Components";
import { SelectInput } from "@/app/(components)/generics/inputs/default/SelectInput/SelectInput";
import CircleLoader from "@/app/(components)/generics/loaders/CircleLoader/CircleLoader";
import PointsLoader from "@/app/(components)/generics/loaders/PointsLoader/PointsLoader";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import Avatars from "@/app/(components)/specifics/tools/ClientAvatar/components/Avatars/Avatars";
import ClientAvatarMenu from "@/app/(components)/specifics/tools/ClientAvatar/components/ClientAvatarMenu/ClientAvatarMenu";
import ExtractAvatar from "@/app/(components)/specifics/tools/ClientAvatar/components/ExtractAvatar/ExtractAvatar";
import GenerateAvatarWithAi from "@/app/(components)/specifics/tools/ClientAvatar/components/GenerateAvatarWithAi/GenerateAvatarWithAi";
import NoOfferCard from "@/app/(components)/specifics/tools/ClientAvatar/components/NoOfferCard/NoOfferCard";
import { useAuthUser } from "@/app/(components)/specifics/wrappers/AuthProvider/AuthProvider";
import { useCanRunPaidActions } from "@/app/(components)/specifics/wrappers/BrandProvider/BrandProvider";
import PageWrapper from "@/app/(components)/specifics/wrappers/PageWrapper/PageWrapper";
import { useInitialSelection } from "@/app/(components)/specifics/wrappers/SelectionProvider/SelectionProvider";
import { useFetchClientAvatarBootstrap } from "@/app/hooks/fetchs/client-avatar/useFetchClientAvatarBootstrap";
import { useFetchProjectOffers } from "@/app/hooks/fetchs/offers/useFetchProjectOffers";
import { useFetchHasProjectInsights } from "@/app/hooks/fetchs/project/useFetchHasProjectInsights";
import { useFetchRunningJobs } from "@/app/hooks/fetchs/user/useFetchRunningJobs";
import { ModalStore } from "@/app/stores/ModalStore";
import { ProjectStore } from "@/app/stores/ProjectStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ClientAvatarBootstrap } from "@/app/types/client-avatar/ClientAvatarBootstrap";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { PageBootstrap } from "@/app/types/common/WorkspaceSelection";
import { AvatarExtractionJobMetadata } from "@/app/types/job/ProcessingJob";
import { UCookie } from "@/app/utils/UCookie";
import { Limits } from "@/app/vars/Limits";
import { QueryKey } from "@/app/vars/QueryKeys";
import { CookieKey, WORKSPACE_COOKIE_MAX_AGE } from "@/app/vars/Storage";
import styles from "./ClientAvatar.module.css";

type Props = DefaultProps & {
  bootstrap: PageBootstrap<ClientAvatarBootstrap> | null;
};

export default function ClientAvatar(props: Props) {
  const { params, bootstrap } = props;
  const { lang } = params;

  const T = useT();
  const user = useAuthUser();
  const canRunPaidActions = useCanRunPaidActions();
  const project = ProjectStore((state) => state.currentProject);
  const projects = ProjectStore((state) => state.projects);
  const setCurrentProject = ProjectStore((state) => state.setCurrentProject);
  const projectsFetched = ProjectStore((state) => state.projectsFetched);
  const queryClient = useQueryClient();

  const initialSelection = useInitialSelection();

  const [selectedOfferId, setSelectedOfferId] = useState<string>(
    initialSelection.offerId ? String(initialSelection.offerId) : "",
  );

  // Collapse the has-insights + offers -> avatars chain into one round-trip on first load and
  // seed every downstream cache. The hooks below are gated on it so they read the seeds; the
  // child Avatars reads the seeded offer avatars and the selection effect converges on the
  // same offer the server seeded.
  const initialBootstrap = bootstrap?.projectId === project?.id ? bootstrap?.data : undefined;

  const { isSuccess: bootstrapReady } = useFetchClientAvatarBootstrap({
    projectId: project?.id,
    enabled: !!user && !!project?.id,
    initialData: initialBootstrap,
  });

  const { data: hasProjectInsightsFresh } = useFetchHasProjectInsights(project?.id, bootstrapReady);
  const hasInsights = hasProjectInsightsFresh ?? (project?._count?.callInsights ?? 0) > 0;
  const { data: fetchedOffers } = useFetchProjectOffers({
    projectId: project?.id,
    enabled: !!user && !!project?.id && bootstrapReady,
  });

  const offers = useMemo(() => fetchedOffers ?? [], [fetchedOffers]);
  const offersLoaded = fetchedOffers !== undefined;

  const selectedOfferHasAvatar =
    (offers.find((o) => o.id.toString() === selectedOfferId)?._count?.clientAvatars ?? 0) > 0;

  useEffect(() => {
    if (!offersLoaded) {
      return;
    }
    if (offers.length === 0) {
      setSelectedOfferId("");
      return;
    }
    if (selectedOfferId && offers.some((o) => o.id.toString() === selectedOfferId)) {
      return;
    }
    const stored = UCookie.read(CookieKey.SELECTED_OFFER);
    const next =
      stored && offers.some((o) => o.id.toString() === stored) ? stored : offers[0].id.toString();
    setSelectedOfferId(next);
    UCookie.write(CookieKey.SELECTED_OFFER, next, WORKSPACE_COOKIE_MAX_AGE);
  }, [offersLoaded, offers, selectedOfferId]);

  const { data: runningJobs } = useFetchRunningJobs({ enabled: !!user });

  const analyzingOfferIds = useMemo(() => {
    if (!runningJobs) return [];
    return runningJobs
      .filter((j) => j.type === ProcessingJobType.CLIENT_AVATAR)
      .map((j) => (j.metadata as AvatarExtractionJobMetadata | null)?.offerId)
      .filter(Boolean) as number[];
  }, [runningJobs]);

  const isAnyAvatarExtracting = useMemo(() => {
    if (!runningJobs) return false;
    return runningJobs.some((j) => j.type === ProcessingJobType.CLIENT_AVATAR);
  }, [runningJobs]);

  const isCurrentOfferAnalyzing = selectedOfferId
    ? analyzingOfferIds.includes(parseInt(selectedOfferId, 10))
    : false;

  const isReady = !user || (projectsFetched && (!project?.id || bootstrapReady));

  const handleOfferCreated = () => {
    if (project?.id) {
      queryClient.invalidateQueries({ queryKey: [QueryKey.PROJECT_OFFER, project.id] });
    }
  };

  const openExtractionModal = (
    targetType: ClientAvatarType,
    isEnrich: boolean,
    rebuild = false,
  ) => {
    if (!project?.id || !selectedOfferId) {
      return;
    }

    ModalStore.getState().openModal(
      Components.AvatarExtraction,
      T(rebuild ? TKey.CLIENT_AVATAR_REEXTRACT_TITLE : TKey.CLIENT_AVATAR_EXTRACT_TITLE) as string,
      {
        params,
        projectId: project.id,
        offerId: parseInt(selectedOfferId, 10),
        targetType,
        isEnrich,
        hasInsights,
        rebuild,
      },
      ModalType.SMALL,
    );
  };

  const openAddProject = () => {
    ModalStore.getState().openModal(
      Components.ProjectForm,
      T(TKey.PROJECT_ADD),
      {
        lang,
        params,
        isCreating: true,
        onAfterSave: () => openExtractionModal(ClientAvatarType.PRIMARY, false),
      },
      ModalType.SMALL,
    );
  };

  const handleExtract = async () => {
    const activeProject = project ?? projects[0];

    if (!activeProject?.id) return;

    if (!project) {
      setCurrentProject(activeProject);
    }

    if (!activeProject.name.trim()) {
      openAddProject();
      return;
    }

    openExtractionModal(ClientAvatarType.PRIMARY, false);
  };

  const analyzingBadge = (
    <div className={styles.analyzingBadge}>
      <CircleLoader size={16} color="var(--color-green-3)" strokeWidth={3} />
      <span className={styles.analyzingText}>{T(TKey.CLIENT_AVATAR_ANALYZING)}</span>
    </div>
  );

  return (
    <PageWrapper
      title={T(TKey.CLIENT_AVATAR_TITLE)}
      subtitle={T(TKey.CLIENT_AVATAR_SUBTITLE)}
      rightElement={
        user &&
        offers &&
        offers.length > 0 &&
        !selectedOfferHasAvatar &&
        !isCurrentOfferAnalyzing ? (
          <div className={styles.rightActions}>
            <ExtractAvatar
              params={params}
              isButton={true}
              atLeastOneAnalysedCall={hasInsights}
              offers={offers}
              onOfferCreated={handleOfferCreated}
              onExtract={handleExtract}
              isAnalyzing={isAnyAvatarExtracting}
            />
          </div>
        ) : undefined
      }
    >
      <div className={styles.container}>
        {!user ? (
          <>
            <div className={styles.emptyActions}>
              <ExtractAvatar
                params={params}
                isButton={false}
                atLeastOneAnalysedCall={undefined}
                offers={undefined}
                onOfferCreated={handleOfferCreated}
                onExtract={handleExtract}
                isAnalyzing={isAnyAvatarExtracting}
              />
              <GenerateAvatarWithAi params={params} />
            </div>
            <Avatars params={params} />
          </>
        ) : !isReady ? (
          <div className={styles.loaderContainer}>
            <PointsLoader color="var(--color-green-3)" delay={Limits.CLIENT_AVATAR_LOADER_DELAY} />
          </div>
        ) : (
          <div className={styles.avatarsWrapper}>
            {offers && offers.length > 0 ? (
              <>
                <div className={styles.offerSelector}>
                  <div className={styles.offerSelectorLeft}>
                    <div className={styles.offerField}>
                      <label className={styles.offerLabel}>
                        {T(TKey.CLIENT_AVATAR_SELECT_OFFER)}
                      </label>
                      <div className={styles.offerSelectWrapper}>
                        <SelectInput
                          className={styles.offerSelect}
                          data={{ offerId: selectedOfferId }}
                          dataKey="offerId"
                          options={offers.map(
                            (o) => [o.id.toString(), o.name] as [string, ReactNode],
                          )}
                          onChange={(d) => {
                            setSelectedOfferId(d.offerId);
                            UCookie.write(
                              CookieKey.SELECTED_OFFER,
                              d.offerId,
                              WORKSPACE_COOKIE_MAX_AGE,
                            );
                          }}
                        />
                        {isCurrentOfferAnalyzing && analyzingBadge}
                      </div>
                    </div>
                  </div>

                  <ClientAvatarMenu params={params} offerId={parseInt(selectedOfferId, 10)} />
                </div>
                {selectedOfferHasAvatar || isCurrentOfferAnalyzing ? (
                  <Avatars
                    params={params}
                    offerId={parseInt(selectedOfferId, 10)}
                    isAnalyzing={isCurrentOfferAnalyzing}
                    onExtract={openExtractionModal}
                    enabled={bootstrapReady}
                  />
                ) : canRunPaidActions ? (
                  <div className={styles.emptyActions}>
                    <ExtractAvatar
                      params={params}
                      isButton={false}
                      atLeastOneAnalysedCall={hasInsights}
                      offers={offers}
                      onOfferCreated={handleOfferCreated}
                      onExtract={handleExtract}
                      isAnalyzing={isAnyAvatarExtracting}
                    />
                    <GenerateAvatarWithAi params={params} />
                  </div>
                ) : (
                  <p className={styles.notGeneratedMessage}>
                    {T(TKey.CLIENT_AVATAR_NOT_GENERATED_MEMBER)}
                  </p>
                )}
              </>
            ) : isAnyAvatarExtracting ? (
              <Avatars params={params} isAnalyzing={true} />
            ) : (
              <div className={styles.emptyActions}>
                {project && <NoOfferCard params={params} />}
              </div>
            )}
          </div>
        )}
      </div>
    </PageWrapper>
  );
}
